import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { apiJson, ApiError } from '@/lib/api/client'
import { formatDate } from '@/lib/utils'
import { PageHeader } from '@/components/page-header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Database,
  Loader2,
} from 'lucide-react'
import { toast } from 'sonner'
import type {
  MirrorStatus,
  MirrorStatusPage,
  MirrorVerifyResult,
} from '@/lib/types/audit'

export const Route = createFileRoute('/_app/admin/audit/mirrors')({
  component: MirrorsPage,
})

function MirrorsPage() {
  const { data, isLoading, error, refetch } = useQuery<MirrorStatusPage, Error>({
    queryKey: ['audit-mirrors'],
    queryFn: () => apiJson<MirrorStatusPage>('/api/admin/audit/mirrors'),
  })

  return (
    <div className="px-10 py-8">
      <Link
        to="/admin/audit"
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-4 no-underline"
      >
        <ArrowLeft className="size-3.5" strokeWidth={1.75} />
        Back to audit log
      </Link>

      <PageHeader
        title="Audit mirrors"
        description="Independent tamper-evident stores receiving every audit row. Each configured mirror is fed by its own pump task; sync-tier rows additionally block the response until the mirror acks."
      />

      {isLoading && (
        <div className="border border-border bg-card/30 rounded-md h-[200px] hawk-shimmer" />
      )}

      {error && (
        <div className="rounded-md border border-yellow-500/30 bg-yellow-500/5 p-4 text-sm text-yellow-200 flex items-start gap-3">
          <AlertTriangle className="size-4 shrink-0 mt-0.5" strokeWidth={1.75} />
          <div className="space-y-1">
            <div className="text-foreground">Failed to load mirror status</div>
            <div className="font-mono text-[11px] text-muted-foreground break-all">
              {error instanceof ApiError
                ? `${error.status} ${error.message}`
                : error.message}
            </div>
          </div>
        </div>
      )}

      {data && data.mirrors.length === 0 && (
        <div className="rounded-md border border-border border-dashed bg-card/30 py-16 text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">
            No mirrors configured
          </div>
          <div className="text-sm text-muted-foreground max-w-md mx-auto">
            Set <code className="text-foreground bg-secondary px-1.5 py-0.5 rounded">HAWK_AUDIT_MIRRORS</code>{' '}
            in your env to replicate audit rows to an external store.
          </div>
        </div>
      )}

      {data && data.mirrors.length > 0 && (
        <div className="space-y-3 mt-6">
          {data.mirrors.map((m) => (
            <MirrorCard
              key={m.id}
              mirror={m}
              tipSeq={data.tip_seq}
              onChange={() => refetch()}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function MirrorCard({
  mirror,
  tipSeq,
  onChange,
}: {
  mirror: MirrorStatus
  tipSeq: number
  onChange: () => void
}) {
  const [from, setFrom] = useState('1')
  const [to, setTo] = useState(String(tipSeq || 1))
  const [verifying, setVerifying] = useState(false)
  const [result, setResult] = useState<MirrorVerifyResult | null>(null)

  const validation = validateRange(from, to)

  const runVerify = async () => {
    if (validation.error || validation.fromN === null || validation.toN === null) return
    setVerifying(true)
    try {
      const r = await apiJson<MirrorVerifyResult>(
        `/api/admin/audit/mirrors/${encodeURIComponent(mirror.id)}/verify`,
        {
          method: 'POST',
          body: JSON.stringify({ from: validation.fromN, to: validation.toN }),
          headers: { 'Content-Type': 'application/json' },
        },
      )
      setResult(r)
      if (r.ok) {
        toast.success(`Verified ${r.checked} rows against ${mirror.id}`)
      } else {
        toast.error(`Mirror verify failed at seq ${r.broken_at_seq}: ${r.reason}`)
      }
      onChange()
    } catch (e) {
      toast.error(`Verify failed: ${(e as Error).message}`)
    } finally {
      setVerifying(false)
    }
  }

  return (
    <div className="rounded-md border border-border/40 bg-card p-5 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Database className="h-4 w-4 text-muted-foreground" />
            <div className="text-base text-foreground">{mirror.id}</div>
            {mirror.healthy ? (
              <span className="flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-300">
                <CheckCircle2 className="h-3 w-3" /> healthy
              </span>
            ) : (
              <span className="flex items-center gap-1 rounded-full bg-yellow-500/10 px-2 py-0.5 text-xs text-yellow-200">
                <AlertTriangle className="h-3 w-3" /> degraded
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            sync_tiers: {mirror.sync_tiers.length === 0 ? 'none (async only)' : mirror.sync_tiers.join(', ')}
            {' / '}on_timeout: {mirror.on_timeout} / timeout: {mirror.timeout_ms}ms
          </div>
        </div>
        <div className="text-right text-xs text-muted-foreground space-y-1">
          <div>lag: <span className="text-foreground">{mirror.lag}</span></div>
          <div>last_acked_seq: <span className="text-foreground">{mirror.last_acked_seq}</span></div>
          {mirror.last_success_at && <div>last success: {formatDate(mirror.last_success_at)}</div>}
        </div>
      </div>

      {mirror.last_error && (
        <div className="rounded border border-yellow-500/20 bg-yellow-500/5 p-3 text-xs text-yellow-200">
          last_error: {mirror.last_error}
          {mirror.consecutive_errors > 0 && (
            <span className="ml-2 text-yellow-200/70">
              ({mirror.consecutive_errors} consecutive)
            </span>
          )}
        </div>
      )}

      <div className="rounded border border-border/40 bg-popover p-3 space-y-2">
        <div className="text-xs text-muted-foreground">Verify a seq range against this mirror</div>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="text-xs text-muted-foreground">From seq</label>
            <Input value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="flex-1">
            <label className="text-xs text-muted-foreground">To seq</label>
            <Input value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <Button onClick={runVerify} disabled={verifying || !!validation.error}>
            {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify range'}
          </Button>
        </div>
        {validation.error && (
          <div className="text-xs text-yellow-200">{validation.error}</div>
        )}
        {result && !validation.error && (
          <div className="text-xs text-muted-foreground">
            {result.ok
              ? `OK: ${result.checked} rows match`
              : `Broken at seq ${result.broken_at_seq}: ${result.reason ?? 'unknown'}`}
          </div>
        )}
      </div>
    </div>
  )
}

function validateRange(
  from: string,
  to: string,
): { fromN: number | null; toN: number | null; error: string | null } {
  const fromN = parseRange(from)
  const toN = parseRange(to)
  if (fromN === null) return { fromN, toN, error: 'from: enter a positive integer' }
  if (toN === null) return { fromN, toN, error: 'to: enter a positive integer' }
  if (fromN < 1) return { fromN, toN, error: 'from must be >= 1' }
  if (toN < fromN) return { fromN, toN, error: 'to must be >= from' }
  return { fromN, toN, error: null }
}

function parseRange(s: string): number | null {
  const trimmed = s.trim()
  if (!trimmed) return null
  const n = Number(trimmed)
  if (!Number.isFinite(n) || !Number.isInteger(n)) return null
  return n
}
